import React from 'react';
import SEO from '../components/SEO';

const Terms: React.FC = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Terms and Conditions',
    description: 'TurboBetto terms and conditions of use',
    url: 'https://turbobetto.com/terms',
  };

  return (
    <>
      <SEO
        title="Terms and Conditions"
        description="Read TurboBetto's terms and conditions to understand the rules and guidelines for using our website."
        url="/terms"
        schema={schema}
      />

      <div className="pt-24 pb-20 min-h-screen bg-dark-bg">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-8">Terms and Conditions</h1>
            <p className="text-gray-400 mb-8">Last updated: December 2024</p>

            <div className="space-y-8">
              <section className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">1. Acceptance of Terms</h2>
                <p className="text-gray-300 leading-relaxed">
                  By accessing and using TurboBetto.com, you accept and agree to be bound by these
                  Terms and Conditions. If you do not agree with any part of these terms, you must
                  not use our website.
                </p>
              </section>

              <section className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">2. Use of Website</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  You agree to use TurboBetto.com only for lawful purposes and in a way that does
                  not infringe the rights of others or restrict their use of the website. You must not:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>Use the website in any way that violates applicable laws</li>
                  <li>Attempt to gain unauthorized access to any part of the website</li>
                  <li>Transmit any viruses, malware, or harmful code</li>
                  <li>Copy, reproduce, or redistribute content without permission</li>
                </ul>
              </section>

              <section className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">3. Age Restrictions</h2>
                <p className="text-gray-300 leading-relaxed">
                  This website is intended for users who are 18 years of age or older. By using this
                  website, you confirm that you are at least 18 years old and of legal gambling age
                  in your jurisdiction. We reserve the right to verify your age at any time.
                </p>
              </section>

              <section className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">4. Content and Information</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  While we strive to provide accurate and up-to-date information, we make no
                  warranties about the completeness, accuracy, or reliability of any information
                  on this website. Casino offers, bonuses, and terms may change without notice.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  You should always verify current terms and conditions directly with the casino
                  before signing up or claiming any bonus.
                </p>
              </section>

              <section className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">5. Affiliate Relationships</h2>
                <p className="text-gray-300 leading-relaxed">
                  TurboBetto may receive compensation from casinos when you click on links or sign
                  up through our website. This does not affect our editorial independence or the
                  accuracy of our reviews. We only recommend casinos that meet our quality standards.
                </p>
              </section>

              <section className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">6. Responsible Gambling</h2>
                <p className="text-gray-300 leading-relaxed mb-4">
                  We promote responsible gambling and encourage players to:
                </p>
                <ul className="list-disc list-inside text-gray-300 space-y-2">
                  <li>Only gamble with money you can afford to lose</li>
                  <li>Set limits on time and money spent gambling</li>
                  <li>Never chase losses</li>
                  <li>Seek help if gambling becomes a problem</li>
                </ul>
                <p className="text-gray-300 leading-relaxed mt-4">
                  For support, visit{' '}
                  <a
                    href="https://www.begambleaware.org/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-yellow-400 hover:underline"
                  >
                    BeGambleAware.org
                  </a>
                </p>
              </section>

              <section className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">7. Intellectual Property</h2>
                <p className="text-gray-300 leading-relaxed">
                  All content on TurboBetto.com, including text, graphics, logos, and images, is
                  the property of TurboBetto or its content suppliers and is protected by copyright
                  and other intellectual property laws.
                </p>
              </section>

              <section className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">8. Limitation of Liability</h2>
                <p className="text-gray-300 leading-relaxed">
                  TurboBetto shall not be liable for any indirect, incidental, special, or
                  consequential damages arising from your use of this website. We are not
                  responsible for any losses incurred from gambling at casinos featured on our site.
                </p>
              </section>

              <section className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">9. Changes to Terms</h2>
                <p className="text-gray-300 leading-relaxed">
                  We reserve the right to modify these Terms and Conditions at any time. Changes
                  will be effective immediately upon posting. Your continued use of the website
                  constitutes acceptance of any changes.
                </p>
              </section>

              <section className="bg-dark-card border border-dark-border rounded-xl p-8">
                <h2 className="text-2xl font-bold text-white mb-4">10. Contact Information</h2>
                <p className="text-gray-300 leading-relaxed">
                  If you have questions about these Terms and Conditions, please contact us at:
                  <br />
                  <a href="mailto:legal@turbobetto.com" className="text-yellow-400 hover:underline">
                    legal@turbobetto.com
                  </a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Terms;

